import React, { useMemo } from 'react';
import {
  useSubscriptions,
  useSubscriptionsDispatch,
} from '@features/subscriptions/context/subscription-context';
import { Button, ButtonLink, FlexView, Tag } from '@goodfynd/ui';
import { addClassNames } from '@goodfynd/theme';
import { Text } from '@goodfynd/ui';
import { renderPrice } from '@utils/number-util';
import { getMarketingUrl } from '@utils/path-util';

import { SubscriptionLandingPages } from './helpers';
import { SubscriptionProductQuantity } from './SubscriptionProductQuantity';
import type { SubscriptionProductProps } from './types';

import classes from './SubscriptionProducts.module.css';

export const SubscriptionProduct = ({
  className,
  frequency,
  lineItem,
  product,
}: SubscriptionProductProps) => {
  const marketingUrl = getMarketingUrl(SubscriptionLandingPages[product.sku]);

  const { activeSubscriptions } = useSubscriptions();
  const { addCartItem, removeCartItem, updateCartItem } =
    useSubscriptionsDispatch();

  const isSubscribed = useMemo(() => {
    return activeSubscriptions.find((subscription) =>
      subscription.items.find((item) => item.price.sku === product.sku)
    );
  }, [activeSubscriptions, product.sku]);

  const renderAddToCart = () => {
    const priceId = product.prices.find(
      (price) => price.frequency === frequency
    )?.stripeId;
    if (!priceId) {
      return null;
    }

    if (
      lineItem &&
      lineItem.previousQuantity &&
      lineItem.previousQuantity !== lineItem.quantity &&
      lineItem.newQuantity === 0 &&
      !product.allowMultiple
    ) {
      return (
        <Button
          content="Uncancel"
          fullWidth
          size="small"
          onClick={() => {
            updateCartItem({ ...lineItem, quantity: 1 });
          }}
        />
      );
    }

    if (isSubscribed && lineItem && !product.allowMultiple) {
      return (
        <Button
          color="neutral"
          content="Cancel"
          fullWidth
          size="small"
          onClick={() => {
            updateCartItem({ ...lineItem, quantity: lineItem.quantity - 1 });
          }}
        />
      );
    }

    if (lineItem?.quantity && product.allowMultiple) {
      return <SubscriptionProductQuantity lineItem={lineItem} />;
    }

    if (lineItem?.quantity) {
      return (
        <Button
          color="danger"
          content="Remove"
          fullWidth
          onClick={() => removeCartItem(product.sku)}
          size="small"
        />
      );
    }

    const seatsLabel = lineItem?.quantity ? 'Change seats' : 'Add seats';
    return (
      <Button
        content={isSubscribed ? seatsLabel : 'Add to cart'}
        fullWidth
        size="small"
        onClick={() =>
          addCartItem({
            priceId,
            quantity: 1,
            newQuantity: 1,
            sku: product.sku,
          })
        }
      />
    );
  };

  const selectedPrice = product.prices.find(
    (price) => price.frequency === frequency
  );
  const price = selectedPrice?.price || 0;
  return (
    <div className={addClassNames(classes['subscription-product'], className)}>
      <div className={classes['product-header']}>
        {product.images[0] && (
          <div
            className={classes['product-image']}
            style={{
              backgroundImage: `url(${product.images[0]})`,
            }}
          />
        )}

        <div className={classes.productText}>
          <div>
            <Text as="subhead2">{product.name}</Text>

            {!!product.description && (
              <Text as="supportingText" color="TextIconSecondary">
                {product.description}
              </Text>
            )}
          </div>

          <div className={classes.priceRow}>
            <Text>{price ? `${renderPrice(price)}/${frequency}` : 'FREE'}</Text>
            {!!selectedPrice?.trialPeriod && (
              <Tag
                label={`${selectedPrice.trialPeriod}-day free trial`}
                type="positive"
              />
            )}
          </div>
        </div>
      </div>

      <FlexView className={classes['button-container']} direction="column">
        {renderAddToCart()}

        {!!marketingUrl && !isSubscribed && (
          <ButtonLink
            color="neutral"
            content="Learn more"
            fullWidth
            href={marketingUrl}
            rank="tertiary"
            size="small"
            target="_blank"
          />
        )}
      </FlexView>
    </div>
  );
};
